import { useCallback, useEffect, useMemo, useState } from "react";
import { Box, Button, Grid, Link, Typography } from "@mui/material";
import { makeStyles } from "@mui/styles";
import { useHistory } from "react-router";
import { useUpdateEffect } from "react-use";
import { findIndex, orderBy } from "lodash";

import Alert from "components/shared/Alert";
import RightContent from "components/wrappers/RightContent";
import { DESIGNS } from "helpers/apis";
import useAxios from "helpers/axios";
import { IDesign } from "types/design";

import { BulkAction, DesignActionType } from "./components/BulkAction";
import { DataTable } from "./components/DataTable";
import { UnpublishConfirmDialog } from "./components/UnpublishConfirmDialog";

const useStyles = makeStyles({
  header: {
    "& > h1": {
      fontSize: 30,
      lineHeight: "43.74px",
      letterSpacing: 0.5,
    },
  },
  alertRoot: {
    justifyContent: "center",
    marginTop: 16,
  },
  alertMessage: {
    fontWeight: 700,
    fontSize: "9.8px",
    lineHeight: "14px",
    letterSpacing: "0.5px",
    textTransform: "uppercase",
  },
});

export const Designs = () => {
  const history = useHistory();

  const [currentPage, setCurrentPage] = useState<number>(1);
  const [rowsData, setRowsData] = useState<IDesign[]>([]);
  const [designUploaded, setDesignUploaded] = useState(false);
  const [message, setMessage] = useState("");
  const [hasUploadError, setHasUploadError] = useState(false);
  const [selectedDesigns, setSelectedDesigns] = useState<number[]>([]);
  const [openUnpublishConfirmDlg, setOpenUnpublishConfirmDlg] = useState(false);
  const [designsToUnpublish, setDesignsToUnpublish] = useState<string[]>([]);

  const isSelectedMessage = useMemo(
    () => message.match("selected."),
    [message]
  );
  const classes = useStyles();

  const onSuccess = useCallback(({ response }: any) => {
    const isPublished = response[0].data.status === "Published";
    let hasFailed = false;

    setRowsData((rowsData) => {
      const result = [...rowsData];
      response.forEach(({ data, code }: { data: IDesign; code: number }) => {
        if (code !== 200 || !data.id) {
          hasFailed = true;
        } else {
          const designIndex = findIndex(result, { id: data.id });
          result.splice(designIndex, 1, data);
        }
      });
      return result;
    });
    setMessage(
      hasFailed
        ? ""
        : isPublished
        ? "Designs published successfully."
        : "Designs unpublished successfully."
    );
    setHasUploadError(hasFailed);
    setSelectedDesigns([]);
  }, []);

  const { fetchData } = useAxios({
    config: {
      url: DESIGNS,
      method: "PUT",
    },
    callOnInit: false,
    successCallback: onSuccess,
    errorCallback: () => {
      setHasUploadError(true);
      setMessage("");
    },
  });

  const handleUploadDesigns = useCallback(
    (event: React.MouseEvent<HTMLElement>) => {
      const uploadDesignsPath = "/upload-designs";

      if (event.ctrlKey) {
        window.open(uploadDesignsPath, "_blank");
      } else {
        history.push(uploadDesignsPath);
      }
    },
    /* eslint-disable react-hooks/exhaustive-deps */
    []
  );

  // Display a message once designs are uploaded successfully
  useEffect(() => {
    const isDesignUploaded =
      (history as any)?.location?.state?.from === "upload-designs";
    setDesignUploaded(isDesignUploaded);
    history.replace({ ...history.location, state: undefined });
    if (isDesignUploaded) {
      setMessage("Your designs have been uploaded successfully.");
    }
  }, []);
  // After design upload, change the default sort by descending upload_date
  useEffect(() => {
    if (designUploaded) {
      setRowsData((rowsData) => [
        ...orderBy(
          rowsData,
          ["uploadedAt", "workflow", "designType", "name"],
          ["desc", "asc", "asc", "asc"]
        ),
      ]);
    } else {
      setRowsData((rowsData) => [
        ...orderBy(
          rowsData,
          ["workflow", "designType", "name"],
          ["asc", "asc", "asc"]
        ),
      ]);
    }
  }, [designUploaded, rowsData.length]);

  // Display selected designs count message
  useUpdateEffect(() => {
    if (selectedDesigns.length > 0) {
      setMessage(
        `${selectedDesigns.length} design${
          selectedDesigns.length > 1 ? "s are" : " is"
        } selected.`
      );
    } else {
      setMessage((message) => (message.match("selected.") ? "" : message));
    }
  }, [selectedDesigns]);

  const confirmAction = useCallback(
    (action: DesignActionType) => {
      fetchData(
        {
          params: {
            validationOnly: false,
          },
          data: selectedDesigns.map((designId) => ({
            id: designId,
            status: action,
          })),
        },
        (data: any) => {
          // Unpublish designs
          setRowsData((rowsData) => {
            return rowsData.map((design) => {
              if (designsToUnpublish.includes(design.mpId!)) {
                return {
                  ...design,
                  status: "Unpublished",
                } as IDesign;
              } else {
                return design;
              }
            });
          });
          onSuccess(data);
        }
      );
    },
    [selectedDesigns, designsToUnpublish]
  );

  const handleAction = useCallback(
    (action: DesignActionType) => {
      if (selectedDesigns.length) {
        if (action === "Unpublished") {
          confirmAction(action);
        } else {
          fetchData(
            {
              params: {
                validationOnly: true,
              },
              data: selectedDesigns.map((designId) => ({
                id: designId,
                status: action,
              })),
            },
            ({ response }: any) => {
              if (
                Array.isArray(response) &&
                response.filter((item) => item.code >= 400).length
              ) {
                setDesignsToUnpublish(
                  response
                    .filter(
                      (item) => item.code >= 400 && item?.data?.status?.mpId
                    )
                    .map((item) => item.data.status.mpId)
                );
                setOpenUnpublishConfirmDlg(true);
              } else {
                setDesignsToUnpublish([]);
                confirmAction(action);
              }
            }
          );
        }
      }
    },
    [selectedDesigns]
  );

  const handlePageChange = useCallback(
    (page: number) => {
      if (!isSelectedMessage) {
        setMessage("");
      }
      setHasUploadError(false);
      setCurrentPage(page);
      if (designUploaded) {
        setDesignUploaded(false);
      }
    },
    [designUploaded, isSelectedMessage]
  );

  return (
    <RightContent gap="24px">
      <Grid
        container
        justifyContent="space-between"
        alignItems="center"
        className={classes.header}
      >
        <Typography variant="h1">Designs</Typography>
        <Button
          color="primary"
          variant="contained"
          onClick={handleUploadDesigns}
        >
          Upload designs
        </Button>
      </Grid>

      {message && (
        <Alert
          severity="info"
          noDismiss
          {...(isSelectedMessage && { icon: false })}
          isFullWidthText={!!isSelectedMessage}
        >
          {isSelectedMessage ? (
            <Box
              display="flex"
              justifyContent={isSelectedMessage ? "center" : "flex-start"}
            >
              {message}
              <Link
                color="primary"
                onClick={() => setSelectedDesigns([])}
                style={{ fontWeight: 600, marginLeft: 8 }}
              >
                Clear Selection.
              </Link>
            </Box>
          ) : (
            message
          )}
        </Alert>
      )}

      {hasUploadError && (
        <Alert
          severity="warning"
          noDismiss
          icon={false}
          classes={{ root: classes.alertRoot, message: classes.alertMessage }}
        >
          We're experiencing some problems. Try again.
        </Alert>
      )}

      <Box display="flex" justifyContent="flex-end">
        <BulkAction
          onAction={handleAction}
          disabled={selectedDesigns.length === 0}
        />
      </Box>

      <DataTable
        currentPage={currentPage}
        filteredRows={rowsData}
        rowsData={rowsData}
        selectedDesigns={selectedDesigns}
        setSelectedDesigns={setSelectedDesigns}
        setCurrentPage={handlePageChange}
        setRowsData={setRowsData}
      />

      <UnpublishConfirmDialog
        open={openUnpublishConfirmDlg}
        setOpen={setOpenUnpublishConfirmDlg}
        designs={designsToUnpublish}
        onConfirm={(isConfirmed) => {
          if (isConfirmed) {
            confirmAction("Published");
          }
          setOpenUnpublishConfirmDlg(false);
        }}
      />
    </RightContent>
  );
};
